// /assets/filter-bar.js

customElements.get('filter-bar') ||
  customElements.define(
    'filter-bar',
    class extends HTMLElement {
      constructor() {
        super();
        this._updating = false;
        this._clickHandler = this._handleClick.bind(this);
        this._documentClickHandler = this._handleDocumentClick.bind(this);
        this._keydownHandler = this._handleKeydown.bind(this);
        this._filtersUpdatedHandler = this._handleFiltersUpdated.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this._clickHandler);
        document.addEventListener('click', this._documentClickHandler);
        document.addEventListener('keydown', this._keydownHandler);
        document.addEventListener('theme:filters:updated', this._filtersUpdatedHandler);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._clickHandler);
        document.removeEventListener('click', this._documentClickHandler);
        document.removeEventListener('keydown', this._keydownHandler);
        document.removeEventListener('theme:filters:updated', this._filtersUpdatedHandler);
      }

      _closeAllDropdowns() {
        this.querySelectorAll('[data-filter-bar-toggle]').forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
          const dropdown = document.getElementById(toggle.getAttribute('aria-controls'));
          if (dropdown) dropdown.hidden = true;
        });
      }

      _updateGroupBadge(group) {
        const toggle = group.querySelector('[data-filter-bar-toggle]');
        if (!toggle) return;
        const activeCount = group.querySelectorAll('.filter-bar__option.is-active').length;
        let badge = toggle.querySelector('.filter-bar__badge');
        if (activeCount > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'filter-bar__badge';
            toggle.insertBefore(badge, toggle.querySelector('svg') || null);
          }
          badge.textContent = activeCount;
          group.classList.add('is-active');
        } else {
          if (badge) badge.remove();
          group.classList.remove('is-active');
        }
      }

      _cff() {
        const section = this.closest('[data-section-type]');
        return section && section.querySelector('collection-filters-form');
      }

      _handleClick(e) {
        // Mobile drawer toggle. On initial page load, theme.dev.js's ariaToggle() and
        // collection-component's MutationObserver handle this (they both rely on
        // [data-aria-toggle] being present). After _handleFiltersUpdated replaces
        // this.innerHTML, those per-element listeners are orphaned — only our delegated
        // listener still fires. We detect the handoff via e.defaultPrevented: theme.dev.js
        // calls preventDefault(), so if it's already false we take over.
        // Use #filter-groups.expanded as truth (not aria-expanded) to avoid stale state.
        const drawerToggle = e.target.closest('[data-aria-toggle]');
        if (drawerToggle && !e.defaultPrevented) {
          e.preventDefault();
          const filterGroups = document.getElementById(drawerToggle.getAttribute('aria-controls'));
          const isOpen = filterGroups && filterGroups.classList.contains('expanded');
          drawerToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          if (filterGroups) {
            filterGroups.classList.toggle('expanded', !isOpen);
            if (!isOpen) {
              filterGroups.classList.add('drawer--animated');
              document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
            } else {
              document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
            }
          }
          return;
        }

        // Desktop dropdown toggle
        const toggle = e.target.closest('[data-filter-bar-toggle]');
        if (toggle) {
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          this._closeAllDropdowns();
          if (!isOpen) {
            toggle.setAttribute('aria-expanded', 'true');
            const dropdown = document.getElementById(toggle.getAttribute('aria-controls'));
            if (dropdown) dropdown.hidden = false;
          }
          return;
        }

        // Sort option — must be checked BEFORE .filter-bar__option because sort options
        // share that class. Delegates to the hidden .popout--sort [data-sort-link] so
        // collection-filters-form handles the AJAX (same path as slide-out / inline).
        const sortOption = e.target.closest('[data-sort-bar-option]');
        if (sortOption) {
          e.preventDefault();
          const sortValue = sortOption.getAttribute('data-sort-bar-option');
          const sortGroup = sortOption.closest('[data-filter-group]');
          if (sortGroup) {
            sortGroup.querySelectorAll('[data-sort-bar-option]').forEach(opt => {
              opt.classList.toggle('is-active', opt.getAttribute('data-sort-bar-option') === sortValue);
            });
          }
          const section = this.closest('[data-section-type]');
          const hiddenLink = section &&
            section.querySelector(`.popout--sort [data-value="${CSS.escape(sortValue)}"]`);
          if (hiddenLink) hiddenLink.click();
          this._closeAllDropdowns();
          return;
        }

        // Filter option — delegate AJAX to collection-filters-form.submitForm
        const option = e.target.closest('.filter-bar__option');
        if (option) {
          e.preventDefault();
          const group = option.closest('[data-filter-group]');
          const isActive = option.classList.contains('is-active');
          option.classList.toggle('is-active', !isActive);
          option.setAttribute('aria-pressed', String(!isActive));
          if (group) this._updateGroupBadge(group);
          const url = option.getAttribute('href');
          if (url && url !== '#') {
            const cff = this._cff();
            if (cff && typeof cff.submitForm === 'function') cff.submitForm(null, url);
          }
          this._closeAllDropdowns();
          return;
        }

        // [data-filter-update-url] fallback (desktop clear link, etc.)
        const filterUrlEl = e.target.closest('[data-filter-update-url]');
        if (filterUrlEl) {
          e.preventDefault();
          const url = filterUrlEl.getAttribute('href');
          if (url) {
            const cff = this._cff();
            if (cff && typeof cff.submitForm === 'function') cff.submitForm(null, url);
          }
          return;
        }
      }

      _handleDocumentClick(e) {
        if (!this.contains(e.target)) this._closeAllDropdowns();
      }

      _handleKeydown(e) {
        if (e.key === 'Escape') this._closeAllDropdowns();
      }

      // Re-sync the filter bar from the server after any AJAX update.
      _handleFiltersUpdated() {
        if (this._updating) return;
        this._updating = true;

        const filterGroups = document.getElementById('filter-groups');
        const wasExpanded = filterGroups && filterGroups.classList.contains('expanded');

        fetch(window.location.pathname + window.location.search)
          .then(r => r.text())
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newBar = doc.querySelector('filter-bar');
            if (newBar) {
              this.innerHTML = newBar.innerHTML;
              if (wasExpanded) {
                const mobileToggle = this.querySelector('[data-aria-toggle]');
                if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');
              }
            }
          })
          .catch(() => {})
          .then(() => { this._updating = false; });
      }
    }
  );
